/**
 * Pipeline de publication — SPEC-MOTEUR §4.9, §6.1, §5.1.
 *
 * C'est le cœur de « encore une vidéo » : il prend les scores d'Atelier, les transforme en
 * facteurs figés, tire la chance, classe la vidéo, inscrit sa courbe et bouge les jauges.
 * Tous les facteurs sont **figés dans le `VideoRecord`** à la publication (§4.1) : rejouer
 * la même action au même turn redonne exactement la même vidéo.
 *
 * Le tirage d'événement de publication (pas 9 du §4.9) N'est PAS ici : c'est le hook
 * `tirerEvenement` de la file de résolution (M1.15).
 */
import { HISTORIQUE_ATELIER_MAX, HISTORIQUE_ALIGNEMENT_MAX, bandeAlignement, recalculerImage, } from "./actions.js";
import { noterDepenseProduction, noterEtapes, noterRevenu } from "./axe-produit.js";
import { consommerProchaineProd } from "./boutique.js";
import { devisEmbauches, enregistrerEmbauche, livraison, livraisonDirigee, periodeMarche, } from "./freelances.js";
import { bonusPorteeHype, CLIP_VIRAL_P_BONUS } from "./fil.js";
import { alignementEffectif, systemes } from "./menage.js";
import { H_SILENCE_TOLERE, H_THEME_EPUISE } from "./clock.js";
import { NICHE_FIDELITE, NICHE_RENTE_MULT, enregistrerThemePublie, ligneNiche, primeNicheDue, } from "./niche.js";
import { construireCourbe } from "./curves.js";
import { POTENTIEL_BASE, collectModifiers, hasCapability, hasContentFlag, } from "./effects.js";
import { FLAG_FENETRE_OPPORTUNITE, windowBoostActif } from "./trends.js";
import { computeVideoOutcome, rythmeDeLaVideo } from "./formula.js";
import { bonusRetourEnForme, cloturerPauseAuRetour } from "./pause.js";
import { plafondCompetence, scoreEffectifAtelier, } from "./progress.js";
import { draw } from "./rng.js";
import { appliquerTraitJauge } from "./traits.js";
import { SEUIL_EVERGREEN, } from "./types.js";
// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Multiplicateurs d'Accroche **fixes** des phases de tendance (SPEC-CONTENU §3.5).
 * Seul `mult_pic` varie par tendance (**arbitrage A18** : le « pic dégressif 2,5 → 2,0 » de
 * SPEC-MOTEUR §4.4 est abandonné au profit du `mult_pic` statique du contenu).
 *
 * Recopiés ici plutôt qu'importés de `content/schema.ts` pour garder `engine/` **libre de
 * Zod**. Un test verrouille l'égalité entre les deux sources.
 */
export const TREND_MULT_MOTEUR = { emergente: 1.3, saturee: 0.9, morte: 0.6 };
/** Potentiel d'Accroche hors tendance (§4.4). */
export const ACCROCHE_FROID = 0.6;
/** Hors tendance mais à potentiel evergreen : « démarrage faible » (PLAN §4.6). */
export const ACCROCHE_FROID_EVERGREEN = 0.8;
/** Malus « me-too » sur tendance saturée quand la Qualité ne suit pas (§4.4). */
export const ACCROCHE_SATUREE_ME_TOO = 0.7;
/** Plafond de score en mode tolérance accessibilité (PLAN §4.5). */
export const SCORE_CAP_TOLERANCE = 85;
/** Classe nulle : « chance basse » (§6.1). */
export const CHANCE_BASSE = 0.7;
/**
 * Classe nulle : seuil de Qualité de la règle §6.1 — **0,70** (avenant **A-5**, proposition 6,
 * validée le 14/08). La spec figée disait **0,80**.
 *
 * Le défaut que ça corrige n'est pas « c'est trop punitif », c'est **« progresser ne sert à
 * rien »** : en début de run, sans nœud de Qualité, le potentiel vaut 0,8, donc
 * `qualite_eff = 0,3 + score/100 × 0,5` n'atteignait 0,80 **qu'à un score de 100 pile**. Passer
 * de 60 à 90 ne changeait strictement RIEN au risque de flop. À 0,70, le palier tombe à un
 * **score de 80** : le joueur qui s'améliore **franchit un seuil qu'il ressent** — et une vidéo
 * bâclée continue de pouvoir flopper (l'exonération de la phase école a été explicitement
 * rejetée par Mathieu).
 */
export const QUALITE_NULLE = 0.7;
/** Classe nulle : exécution sous ce seuil (§4.8, §6.1). */
export const EXECUTION_NULLE = 30;
/**
 * Evergreen : Qualité_eff exigée (§6.1) — ✎ **abaissée de 1,30 à 0,85** par l'avenant
 * **A-25**, sur décision de Mathieu (14/08).
 *
 * **Le défaut que ça corrige n'est pas « c'est trop dur », c'est « c'est fermé ».** Mesuré sur
 * la campagne de 625 runs : **120 intentions evergreen jouées, 0 vidéo de classe `evergreen`
 * obtenue**. Or `remaster` et `ranger_serie` ont pour précondition commune une vidéo
 * evergreen — les deux mécaniques étaient donc **injouables**, pas « jamais jouées », et avec
 * elles la rente (§6.3), les séries (§6.5) et le nœud *Lore de chaîne*.
 *
 * **L'arithmétique de la fermeture.** `qualite_eff = 0,30 + (S_qual/100) × (P_qualité − 0,30)`.
 * À 1,30 exigé :
 *
 * | exécution | S_qual | potentiel requis | `potential_add` requis | coût dans l'arbre |
 * |---|---|---|---|---|
 * | forte | ≈ 88 | 1,436 | +0,636 | 22 PC + 830 ₵ (+9 pour *L'Intemporelle*) |
 * | moyenne | ≈ 70 | 1,729 | +0,929 | 38 PC + 1 630 ₵ (+9) |
 * | faible | ≈ 45 | 2,522 | +1,722 | **impossible** — au-dessus du plafond de Qualité (2,0) |
 *
 * Une run entière dépense **22 à 36 PC**, tout compris. Le ticket d'entrée valait donc la
 * totalité du budget d'une partie, pour une exécution forte seulement.
 *
 * **D'où vient le 1,30, et pourquoi il ne décrivait pas un objectif.** PLAN §4.6 dit
 * « Qualité ≥ 1,3 » sans préciser *quelle* Qualité, et SPEC-MOTEUR §6.1 l'a lu comme la
 * Qualité **effective**. Or le build de référence de la spec elle-même — §12, « 5 nœuds
 * qualite_potentiel » à 3 300 abonnés — a un **potentiel** de 1,30 : à ce potentiel, la
 * Qualité effective n'atteint 1,30 qu'à un score de **100 pile**. Le seuil décrivait un build
 * maxé jouant parfaitement.
 *
 * **La valeur est MESURÉE, pas déduite — et la déduction s'était trompée.** ⟦A-25⟧ a d'abord
 * posé **1,00** (le build de référence × un score compétent de 70), rejoué la campagne de
 * 625 runs aux mêmes seeds, et retrouvé **exactement les mêmes chiffres** : toujours 0
 * evergreen. Le harnais imprime désormais la distribution qui manquait — Qualité_eff observée
 * sur les longues réellement publiées, par profil d'exécution (§9.2) :
 *
 * | exécution | méd. | p95 | max | tentatives evergreen (méd.) |
 * |---|---|---|---|---|
 * | faible N(45,10) | 0,581 | 0,671 | **0,784** | aucune tentative |
 * | moyenne N(70,8) | 0,724 | 0,848 | 0,913 | 0,841 |
 * | forte N(88,5) | 0,827 | 0,968 | **0,984** | 0,941 |
 *
 * **Le plafond réel du jeu est 0,984**, pas 1,30 : la campagne n'achète jamais le build de
 * référence de §12 (une run dispose de 22 à 36 PC en tout, et les branches de Qualité passent
 * après les contre-mesures). Tout seuil au-dessus de ~0,98 est fermé par construction.
 *
 * **0,85 est donc le seuil qui dit ce que §4.6 veut dire**, lu dans le tableau ci-dessus :
 * une exécution **forte** le franchit largement (médiane 0,941), une **moyenne** le franchit
 * quand son build de Qualité est là (médiane 0,841, p95 0,893), une **faible** ne le franchit
 * **jamais** — son maximum sur 2 641 longues est 0,784. L'evergreen redevient la récompense du
 * métier, et reste fermée à qui bâcle.
 *
 * C'est exactement la méthode d'⟦A-5⟧ sur `QUALITE_NULLE` (0,80 → 0,70, quelques lignes plus
 * haut) : quand un seuil est au-dessus de ce que la formule produit réellement, il ne règle
 * pas la difficulté — il supprime la mécanique.
 *
 * ⚠️ Cette constante ne sert **qu'à la classification**. Le « Fidélité +3 pour une longue
 * réussie » de §5.1 avait la même valeur et lisait la même constante : les deux règles ont
 * été **séparées** (voir `QUALITE_LONGUE_REUSSIE`) — sans quoi baisser le seuil d'evergreen
 * aurait donné de la Fidélité à des joueurs qui ne tentent jamais d'evergreen, et l'état
 * neutre d'⟦A-10⟧ serait tombé en silence.
 */
export const QUALITE_EVERGREEN = 0.85; // ✎ A-25
/**
 * Fidélité +3 d'une **longue réussie** (§5.1, SPEC-MOTEUR §5.1 tableau) — **1,30, inchangé**.
 *
 * Née d'un dédoublement, pas d'un réglage : jusqu'à ⟦A-25⟧ cette règle lisait
 * `QUALITE_EVERGREEN`, par coïncidence de valeur. Les deux seuils ne parlent pourtant pas de
 * la même chose — l'un classe une vidéo, l'autre récompense un public — et rien ne justifie
 * qu'ils bougent ensemble. C'est cette constante qui porte le chiffre de la spec.
 */
export const QUALITE_LONGUE_REUSSIE = 1.3;
/** Rente evergreen : base hebdo et plafond (§6.3). */
export const RENTE_BASE = 0.025;
export const RENTE_PLAFOND = 0.05;
/** Rente d'une vidéo rangée en série : ×1,2 (§6.3, PLAN §4.6). */
export const RENTE_MULT_SERIE = 1.2;
/** Jet viral : 3 %, +1 point si exécution ≥ 90 (§10.2). */
export const VIRAL_P_BASE = 0.03;
export const VIRAL_P_BONUS = 0.01;
export const VIRAL_SEUIL_EXECUTION = 90;
export const VIRAL_CHANCE = 8;
/** Momentum : +0,1 si publication ≤ `H_SILENCE_TOLERE` après la précédente (§5.1). */
export const MOMENTUM_PUBLICATION = 0.1;
/** Zone Fatigue : SM ∈ [30;59] ⇒ Qualité_eff ×0,9 (§11.1). */
export const SM_FATIGUE_MIN = 30;
export const SM_FATIGUE_MAX = 59;
/**
 * Scores retenus pour le calcul : embauches appliquées, puis **plafond de l'étape**.
 *
 * ⟦A-9⟧ Le plafond n'est plus une constante d'accessibilité : c'est le **plafond de
 * compétence**, par étape (`exigenceAtelier(state, stage, content)`), qui porte déjà la
 * borne 85 de la tolérance. C'est ICI que le moteur devient autoritatif là-dessus — le
 * mini-jeu, lui, n'est jamais saboté : le joueur voit son vrai geste, et voit ce que son
 * niveau en retient.
 *
 * `content` est optionnel pour rester rétro-compatible avec les appels de test qui ne
 * fournissent pas d'arbre ; absent, la part d'arbre est **neutre** (voir `partArbreAtelier`),
 * jamais nulle.
 *
 * ⟦A-33 · A-47⟧ Le plafond n'est plus une **coupe** mais une **compression** :
 * `scoreEffectifAtelier` remplace le `clamp` d'⟦A-9⟧, et depuis ⟦A-47⟧ elle est **active**
 * (`K_SURPASSEMENT = 0,60`) — un 98 et un 71 ne rendent plus la même chose sous un plafond de
 * 70. C'est **ce site-ci** qui décide de ce que vaut un geste, et le freelance y passe comme le
 * joueur (`freelances.ts`) ; l'autre plafonnement du moteur (`validateAtelierScore`) est une
 * borne d'échelle anti-triche et reste, lui, une coupe franche.
 */
export function scoresEffectifs(state, scores, format, content, options = {}, embauche) {
    const plafonne = (v, stage) => scoreEffectifAtelier(v, state, stage, content, options);
    const effectifs = {
        ecriture: plafonne(scores.ecriture, "ecriture"),
        tournage: plafonne(scores.tournage, "tournage"),
        montage: plafonne(scores.montage, "montage"),
        // Un short n'a pas ces deux étapes (§1.2) : elles restent null quoi qu'on envoie.
        miniature: format === "short" ? null : plafonne(scores.miniature ?? 0, "miniature"),
        promotion: format === "short" ? null : plafonne(scores.promotion ?? 0, "promotion"),
        delegated: [...scores.delegated],
        ...(scores.freelances === undefined ? {} : { freelances: { ...scores.freelances } }),
    };
    let coutFreelances = 0;
    const embauchees = [];
    if (embauche !== undefined) {
        const modsEmbauche = collectModifiers(state, embauche.content);
        const devis = devisEmbauches(state, embauche.content, embauche.seed, scores, format === "short" ? "short" : "longue", periodeMarche(embauche.clock_marche ?? state.clock_h), 
        // ⟦A-78⟧ La profondeur d'offre à la FACTURE est celle de la validation : mêmes mods,
        // sinon une 3e offre embauchée disparaîtrait en silence au moment de payer.
        modsEmbauche);
        // ⟦A-61 · A-50 §4⟧ **La direction** — l'agrégat n'est lu que s'il y a des embauches, et
        // il vaut 0 partout tant qu'aucun nœud ne porte de `freelance_direction` : l'état neutre
        // ne dépense pas un `collectModifiers` de plus qu'avant sur les parties sans délégation.
        const directionParEtape = modsEmbauche.freelanceDirection;
        for (const ligne of devis.lignes) {
            // ⟦A-61⟧ Ce que le freelance rend **parce que vous savez ce que vous demandez** : il
            // parcourt une part du chemin qui le sépare de ce que la chaîne saurait exploiter. Sans
            // nœud de direction, `livraisonDirigee` rend son entrée au bit près.
            const brut = livraison(ligne.offre, embauche.seed, embauche.turn);
            const dirige = livraisonDirigee(brut, directionParEtape[ligne.stage], plafondCompetence(state, ligne.stage, content, options));
            // ⟦A-9/A-33⟧ Puis la livraison repasse par le **plafond de compétence**, comme
            // n'importe quel geste : payer quelqu'un n'achète pas le droit de dépasser ce que la
            // chaîne sait exploiter.
            effectifs[ligne.stage] = plafonne(dirige, ligne.stage);
            embauchees.push(ligne.offre.id);
        }
        coutFreelances = devis.total;
    }
    return { effectifs, coutFreelances, embauchees };
}
/** Empile les scores joués dans l'historique d'Atelier (10 max par étape, §1.6). */
function majHistoriqueAtelier(state, scores, format) {
    const etapes = format === "short"
        ? ["ecriture", "tournage", "montage"]
        : ["ecriture", "tournage", "montage", "miniature", "promotion"];
    for (const stage of etapes) {
        // ⟦M2.10⟧ Une étape achetée n'entre pas dans l'historique : `atelier_history` est la
        // PRATIQUE du joueur (c'est ce que l'écran d'Atelier lui montre — « tes 5 derniers
        // scores »), et le geste d'un freelance n'est pas le sien. La raison a changé avec le
        // marché — plus de moyenne qui se morde la queue — la règle, non.
        if (scores.delegated.includes(stage))
            continue;
        const v = scores[stage];
        if (v === null || v === undefined)
            continue;
        const histo = state.atelier_history[stage] ?? [];
        histo.push(v);
        if (histo.length > HISTORIQUE_ATELIER_MAX)
            histo.splice(0, histo.length - HISTORIQUE_ATELIER_MAX);
        state.atelier_history[stage] = histo;
    }
}
// ─────────────────────────────────────────────────────────────────────────────
// Pas 4 — potentiel d'Accroche (table §4.4)
// ─────────────────────────────────────────────────────────────────────────────
/** Le slot de tendance sur lequel le sujet se trouve, s'il y en a un (**arbitrage A27**). */
export function slotDuSujet(state, sujet) {
    return state.trends.find((t) => t.trend_id !== "" && t.themes.includes(sujet.theme)) ?? null;
}
/**
 * Potentiel d'Accroche — table fermée §4.4, avec le `mult_pic` du contenu en phase de pic
 * (**arbitrage A18**). Le bonus d'alignement (+0,3 chaos / −0,1 lumière) est appliqué par
 * `computeVideoOutcome`, pas ici.
 */
export function potentielAccroche(state, sujet, content, qualiteEff, bonusArbre = 0) {
    const slot = slotDuSujet(state, sujet);
    let base;
    if (!slot || slot.phase === "morte") {
        base =
            !slot && sujet.potentiel_evergreen >= SEUIL_EVERGREEN
                ? ACCROCHE_FROID_EVERGREEN // « démarrage faible » d'une evergreen (A26)
                : slot
                    ? TREND_MULT_MOTEUR.morte
                    : ACCROCHE_FROID;
    }
    else if (slot.phase === "emergente") {
        base = TREND_MULT_MOTEUR.emergente;
    }
    else if (slot.phase === "pic") {
        const trend = content.trends.find((t) => t.id === slot.trend_id);
        base = trend?.mult_pic ?? TREND_MULT_MOTEUR.emergente;
    }
    else {
        // saturée : 0,9, ou 0,7 si la Qualité ne suit pas (malus « me-too »)
        base = qualiteEff < 1.0 ? ACCROCHE_SATUREE_ME_TOO : TREND_MULT_MOTEUR.saturee;
    }
    return base + bonusArbre;
}
// ─────────────────────────────────────────────────────────────────────────────
// Pas 5 — classification (§6.1)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Classe de la vidéo — §6.1, **dans cet ordre, la première condition vraie gagne**.
 * `potentiel_evergreen ≥ 60` remplace le booléen de la spec (**arbitrage A26**).
 */
export function classifier(p) {
    if (p.execution_score < EXECUTION_NULLE)
        return "nulle";
    if (p.chance < CHANCE_BASSE && p.qualite_eff < QUALITE_NULLE)
        return "nulle";
    // Une evergreen_intent sortie sur tendance est « ratée » → TREND (§6.1 note).
    const phase = p.slot?.phase;
    if (phase === "emergente" || phase === "pic")
        return "trend";
    if (p.evergreen_intent &&
        p.peut_evergreen &&
        p.sujet.potentiel_evergreen >= SEUIL_EVERGREEN &&
        p.qualite_eff >= QUALITE_EVERGREEN) {
        return "evergreen";
    }
    return "standard";
}
/**
 * Taux hebdomadaire de rente **figé** à la publication (§6.3) : base 2,5 %, ×1,4 si SEO —
 * porté par le `multiplier` de cible `rente` du contenu, pas par un id de skill en dur —,
 * ×1,2 si la vidéo est en série, plafonné à 5 %.
 *
 * ⚠️ On lit **`multiplier_skills`**, pas `multiplier` : le §6.3 fige les propriétés
 * PERMANENTES de la chaîne, et un multiplicateur de rente porté par un événement à `duree_h`
 * (il en existe un dans le contenu v1) doit **expirer** — il est donc appliqué au versement
 * par `sleep.ts`, pas gelé ici. Lire `multiplier` aux deux endroits comptait SEO deux fois
 * (1,4² = 1,96, écrasé par le plafond).
 *
 * ⟦SPEC-MOTEUR §7.4 · A-4 C⟧ **`dansLaNiche` ajoute le ×1,15 de la prime de cohérence**, au
 * même endroit et pour la même raison que le ×1,2 des séries : une prime de rente qu'on
 * recalculerait rétroactivement ferait varier le passé de la chaîne à chaque changement de
 * ligne éditoriale. `false` par défaut ⇒ le taux d'avant l'avenant, au chiffre près.
 */
export function tauxRenteFige(mods, enSerie, dansLaNiche = false) {
    const brut = RENTE_BASE *
        (mods.multiplier_skills.rente ?? 1) *
        (enSerie ? RENTE_MULT_SERIE : 1) *
        (dansLaNiche ? NICHE_RENTE_MULT : 1);
    return Math.min(RENTE_PLAFOND, brut);
}
// ─────────────────────────────────────────────────────────────────────────────
// Le pipeline
// ─────────────────────────────────────────────────────────────────────────────
/**
 * `publier` — pas 1 à 8 du §4.9. MUTE `ctx.state`, qui est déjà le clone de travail de la
 * file de résolution (§2.3). Branché comme hook `publier` de `ResolutionHooks`.
 *
 * Le pas 9 (tirage d'événement) appartient au hook `tirerEvenement` (M1.15). Quand le jet
 * viral réussit, un `EngineEvent` de kind `viral` est poussé dans `ctx.events` : **c'est le
 * signal que la viralité EST l'événement du tour** (§10.2) et que le tirage doit être sauté.
 */
export function publier(ctx, action, 
/** L'horloge au début de l'action — voir `ContexteEmbauche.clock_marche`. */
clockMarche) {
    const state = ctx.state;
    const content = ctx.content;
    const format = action.format;
    const sujet = content.subjects.find((s) => s.id === action.subject_id);
    if (!sujet) {
        // La validation (§2.2) l'a déjà écarté ; garde-fou pour ne jamais publier à l'aveugle.
        throw new Error(`publier: sujet inconnu ${action.subject_id}`);
    }
    const mods = collectModifiers(state, content);
    // — 0. Avenant A-2 : cette publication est-elle un RETOUR de pause annoncée ? Clôturer la
    //   pause ici garantit le point 5c — « le bonus ne s'applique qu'à UNE vidéo », puisqu'elle
    //   est refermée dans le même geste.
    //   ⟦SPEC-FIL §2.5 / M3.21⟧ `content` décide lequel des deux effets s'applique — la Hype
    //   (Fil allumé) ou le transitoire Fidélité/image d'⟦A-2⟧ (Fil éteint). Jamais les deux.
    const retour = cloturerPauseAuRetour(state, ctx.events, content);
    // — 1. scores effectifs (délégation, plafond de compétence) —
    //   ⟦A-11 §3⟧ La production value entre ICI, et **nulle part ailleurs** : elle déplace la
    //   borne, jamais le score. Les Clicks ont déjà été débités par la table §2.2 — ce qu'ils
    //   ont acheté, c'est le droit de garder un meilleur geste, pas un meilleur geste.
    //   ⟦A-4 E / M2.10⟧ Les étapes de `delegated` sont **achetées au marché des freelances** :
    //   `scoresEffectifs` y substitue la livraison (cote ± variance) et rend la facture. Le
    //   devis est lisible AVANT (`devisEmbauches`) — c'est ce que le transitoire ne faisait pas.
    const { effectifs, coutFreelances, embauchees } = scoresEffectifs(state, action.scores, format, content, { production_value: action.production_value ?? 0 }, { content, seed: ctx.seed, turn: state.turn, clock_marche: clockMarche });
    if (coutFreelances > 0)
        state.money -= coutFreelances;
    // ⟦SPEC-AXE-PRODUIT §3.1 · §3.2⟧ **Les deux signaux de flux se remplissent ICI**, au seul
    //   endroit qui connaît à la fois le prix payé et le nombre d'étapes sorties de la main du
    //   joueur. La liste des dépenses de production est FERMÉE (§3.1) : l'embauche d'un
    //   freelance ⟦A-4 E⟧ et la production value ⟦A-11 §3⟧ en sont, la thérapie, le respec, la
    //   boutique de vie, les charges et les dons n'en sont pas — ces derniers parce qu'⟦A-4
    //   décision 6⟧ exige qu'ils ne soient « jamais un levier d'optimisation ».
    //
    //   `embauchees.length` et non `scores.delegated.length` : une étape déléguée pour laquelle
    //   aucune offre n'a été retenue garde le score envoyé, donc reste jouée à la main (§3.2 —
    //   « achetée dès que son score ne vient pas de la main du joueur »).
    //
    //   Interrupteur éteint ⇒ les trois appels sortent à leur première ligne, sans rien écrire.
    noterDepenseProduction(state, content, coutFreelances);
    noterDepenseProduction(state, content, action.production_value ?? 0);
    noterEtapes(state, content, format, embauchees.length);
    //   ⟦A-60⟧ **Le matos préparé est consommé ICI**, et pas avant : le pattern d'anti-triche
    //   a été régénéré par le serveur depuis l'état d'AVANT l'action, où l'objet était encore
    //   en file. Le vider après le calcul des scores, c'est ce qui fait de l'aide une aide
    //   d'UNE vidéo — et c'est ce qui garantit que le prochain tour repart de l'état neutre.
    for (const id of consommerProchaineProd(state)) {
        ctx.log?.push({
            turn: state.turn,
            clock_h: state.clock_h,
            text: `« ${id} » a servi sur cette vidéo — il n'en reste rien.`,
        });
    }
    //   La fidélité se compte ici, et nulle part ailleurs : c'est ce compteur qui fait monter la
    //   cote d'une pépite et la garde au marché (`engine/freelances.ts`).
    for (const id of embauchees)
        enregistrerEmbauche(state, id);
    // — 2. scores dérivés + historique d'Atelier —
    majHistoriqueAtelier(state, effectifs, format);
    // — 3. chance, puis jet viral (§10.2) —
    const uChance = draw(ctx.seed, state.turn, "chance_roll", 0);
    let chance = 0.5 + uChance * 1.1; // uniforme [0,5;1,6[
    const executionPrevisionnelle = computeVideoOutcome({
        subscribers: state.subscribers,
        loyalty: state.loyalty,
        momentum: state.momentum,
        scores: effectifs,
        format,
        accroche_potentiel: 1,
        qualite_potentiel: 1,
        diffusion_potentiel: 1,
        chance: 1,
        alignment_band: "neutre",
        nulles_visibles: 0,
    }).execution_score;
    // ⟦SPEC-FIL §4.3⟧ **Le clip viral d'une vanne réussie**, consommé ici. PLAN §4.14 promet
    //   « parfois un clip viral » à la réponse `humour` ; plutôt qu'un second tirage à
    //   équilibrer, la vanne ajoute **un point de probabilité** au jet viral existant, une seule
    //   fois, à la publication suivante. Le drapeau est retiré qu'il ait payé ou non : c'est une
    //   avance sur une vidéo, pas une rente. Fil éteint ⇒ le drapeau n'est jamais posé
    //   (`traiterFil` sort avant), donc `pViral` est au point près celui d'avant ⟦A-10⟧.
    const clip = state.flags.clip_viral_fil === true;
    if (clip)
        state.flags.clip_viral_fil = false;
    const pViral = VIRAL_P_BASE +
        (executionPrevisionnelle >= VIRAL_SEUIL_EXECUTION ? VIRAL_P_BONUS : 0) +
        (clip ? CLIP_VIRAL_P_BONUS : 0);
    const viral = draw(ctx.seed, state.turn, "viral_roll", 0) < pViral;
    if (viral)
        chance = VIRAL_CHANCE;
    // — 4. facteurs effectifs et VuesPic —
    // ⟦C60 · A-177⟧ L'alignement que le moteur lit du sujet : le sien, ou `0` quand l'alignement est
    // coupé (bande neutre, ni bonus chaos, ni malus lumière, ni vues volatiles). Actif (le défaut),
    // la valeur d'avant, au caractère près.
    const sys = systemes(content);
    const alignement = alignementEffectif(sujet.alignement, sys);
    const bande = bandeAlignement(alignement);
    // ⚠️ **Ne JAMAIS relire `state.energy` ici** (audit des formules, B1). Le crunch est
    // « énergie < 20 **au lancement** d'une production » (§2.2) : `actions.ts` le pose au pas 3
    // en lisant l'énergie AVANT de débiter le coût, et `ctx.crunch` porte déjà cette règle.
    // Le `|| state.energy < SEUIL_CRUNCH` qui vivait ici lisait l'énergie APRÈS le débit — donc
    // « il te reste moins de 20 ⚡ une fois la vidéo faite », ce qui n'est pas la même phrase.
    // Mesuré sur 12 555 vidéos : 25,6 % de FAUX crunch contre 11,3 % de légitime, soit un
    // ×0,7 de Qualité (−30 % de vues) sur un quart des publications — et **muet** : ni le
    // `CRUNCH_SM` de −5 ni le signal au joueur n'étaient posés, l'écran de publication ⟦A-8⟧
    // ne pouvait donc rien expliquer. Une evergreen n'y échappait qu'avec 88 ⚡ sur 100.
    const crunch = ctx.crunch;
    const fatigue = state.mental >= SM_FATIGUE_MIN && state.mental <= SM_FATIGUE_MAX;
    // ⟦C5⟧ La fenêtre d'opportunité ×3 (PLAN §4.8) : la condition était TOUJOURS fausse, faute
    //   d'écrivain pour `window_boost_expires_h`. Elle lit maintenant les deux routes — le champ
    //   d'état et le flag de contenu (`trends.ts`, `FLAG_FENETRE_OPPORTUNITE`).
    const windowBoost = windowBoostActif(state, hasContentFlag(state, FLAG_FENETRE_OPPORTUNITE));
    // La Qualité_eff ne dépend pas de l'Accroche : on la calcule d'abord, parce que le malus
    // « me-too » d'une tendance saturée en dépend (§4.4).
    const qualiteSeule = computeVideoOutcome({
        subscribers: state.subscribers,
        loyalty: state.loyalty,
        momentum: state.momentum,
        scores: effectifs,
        format,
        accroche_potentiel: ACCROCHE_FROID,
        qualite_potentiel: mods.potential.qualite,
        diffusion_potentiel: mods.potential.diffusion,
        mult_qualite: mods.multiplier.qualite,
        chance: 1,
        alignment_band: bande,
        nulles_visibles: 0,
        crunch,
        fatigue,
    }).qualite_eff;
    // ⟦C60 · A-177⟧ Sans gestion de la bibliothèque, plus personne ne peut ranger ses vidéos nulles :
    // leur malus ne s'accumule donc plus (il serait une sanction sans remède). Actif, le compte d'avant.
    const nullesVisibles = sys.bibliotheque
        ? state.videos.filter((v) => v.class === "nulle" && v.visibility === "public").length
        : 0;
    const outcome = computeVideoOutcome({
        subscribers: state.subscribers,
        loyalty: state.loyalty,
        momentum: state.momentum,
        scores: effectifs,
        format,
        accroche_potentiel: potentielAccroche(state, sujet, content, qualiteSeule, mods.potential.accroche),
        qualite_potentiel: mods.potential.qualite,
        diffusion_potentiel: mods.potential.diffusion,
        // ⟦A-17⟧ Le `potential_add` de facteur `portee` (nœud *Murmureur d'algorithme*, +0,05)
        //   était agrégé depuis toujours et lu nulle part : le nœud était vendu inerte.
        //
        // ⚠️ ⟦A-26 / A-10⟧ **Le `− POTENTIEL_BASE.portee` n'est pas un détail : c'est l'état
        //   neutre.** `portee_potentiel` est un **BONUS** ajouté APRÈS le clamp (`formula.ts`,
        //   et son propre commentaire de type : « absent ⇒ 0 ⇒ aucune ancre ne bouge »), alors
        //   que `mods.potential.portee` est un **POTENTIEL**, dont la base vaut déjà
        //   `PLANCHER_PORTEE` = 0,10 (`effects.ts`). Passé brut, il offrait **+0,10 de Portée
        //   absolue à CHAQUE publication sans qu'aucun nœud n'ait été acheté** — 0,35 écrêté à
        //   0,40 pour tout le monde, et le nœud à +0,05 n'en rendait que la moitié.
        //   Retrancher la base rend au nœud son +0,05 exact, et rend au joueur qui n'a rien
        //   acheté le 0 que l'avenant lui promettait. Le `max(0, …)` tient le plancher : un
        //   `potential_add` négatif ne doit pas ouvrir un malus de Portée que personne n'a écrit.
        // ⟦SPEC-FIL §2.3 A / M3.21⟧ **Et la Hype**, qui entre par la même porte et n'en a pas
        //   d'autre (§2.3 C : ni conversion, ni qualité, ni revenus, ni poids d'événements). Un
        //   seul point d'entrée = un seul endroit à débrancher si l'équilibrage le demande.
        //   `bonusPorteeHype` rend **exactement 0** à Fil éteint ou à `hype = 0` : la somme est
        //   alors le flottant d'avant, bit pour bit, donc aucune ancre §12 ne bouge ⟦A-10⟧.
        portee_potentiel: Math.max(0, mods.potential.portee - POTENTIEL_BASE.portee) + bonusPorteeHype(state, content),
        // ⟦C4⟧ Les trois cibles de `multiplier` qui visent un facteur effectif. Six cibles sur
        //   neuf n'étaient lues nulle part ; ce sont trois d'entre elles. C'est par `accroche`
        //   que passe la fenêtre d'opportunité ×3 de PLAN §4.8 (`fenetre_panne_reseau`).
        mult_accroche: mods.multiplier.accroche,
        mult_qualite: mods.multiplier.qualite,
        mult_diffusion: mods.multiplier.diffusion,
        chance,
        // ⟦A-35⟧ Le tirage de l'**amplification algorithmique** (§4.5 bis). Il se prend sur
        //   `viral_roll` **index 1** : SPEC-MOTEUR §3.2 est une liste FERMÉE de 9 purposes, et
        //   l'index est libre — aucun autre tirage n'est déplacé, donc aucune run existante ne
        //   diverge par le RNG. Il est **indépendant** du jet de chance et du jet viral : la
        //   poussée de la plateforme n'est pas « la même chance vue deux fois ».
        amplification_roll: draw(ctx.seed, state.turn, "viral_roll", 1),
        alignment_band: bande,
        nulles_visibles: nullesVisibles,
        crunch,
        fatigue,
        window_boost: windowBoost,
    });
    // Les `multiplier` de contenu s'appliquent après la formule (SPEC-CONTENU §2.1).
    // Le bonus de retour de pause (A-2 point 3) multiplie VuesPic, comme la fenêtre
    // d'opportunité — c'est une hype de comeback, pas un changement de qualité.
    // ⟦C46 · A-168 §1⟧ **Le rythme de la partie** : un facteur global, lu dans `content/meta.json`,
    //   qui fait compter une vidéo réussie pour plusieurs (`rythmeDeLaVideo` dit où et pour qui).
    //   Il multiplie les VUES, donc, par la courbe, les abonnés, les Clicks et l'XP d'un seul
    //   geste. Absent ⇒ ×1 ⇒ `vuesPic` est le flottant d'avant, bit pour bit ⟦A-10⟧.
    const vuesPic = Math.round(outcome.vues_pic *
        (mods.multiplier.vues ?? 1) *
        (retour?.mult ?? 1) *
        rythmeDeLaVideo(content, state.subscribers, outcome.execution_score));
    const tc = outcome.tc * (mods.multiplier.conversion ?? 1);
    // — 5. classe, courbe, rente —
    const slot = slotDuSujet(state, sujet);
    const evergreenIntent = format === "longue" ? action.evergreen_intent : false;
    const classe = classifier({
        execution_score: outcome.execution_score,
        chance,
        qualite_eff: outcome.qualite_eff,
        sujet,
        slot,
        evergreen_intent: evergreenIntent,
        peut_evergreen: hasCapability(mods, "production_evergreen"),
    });
    // ⟦SPEC-MOTEUR §7.4 · A-4 C⟧ **La prime de cohérence, décidée AVANT de figer la vidéo.**
    // `primeNicheDue` lit la fenêtre glissante *avec* cette publication dedans : la 3ᵉ vidéo
    // d'un thème crée la niche ET en profite, sinon le bandeau apparaîtrait au tour d'après
    // celui où le joueur l'a mérité. Une `nulle` n'y a pas droit — la cohérence fidélise, à
    // condition que les vidéos tiennent.
    const dansLaNiche = primeNicheDue(state, sujet.theme, classe);
    const video = {
        id: state.videos.length + 1,
        subject_id: sujet.id,
        format,
        class: classe,
        alignment: alignement,
        execution_score: outcome.execution_score,
        stage_scores: effectifs,
        accroche_eff: outcome.accroche_eff,
        qualite_eff: outcome.qualite_eff,
        diffusion_eff: outcome.diffusion_eff,
        chance,
        taux_conversion: tc,
        vues_pic: vuesPic,
        published_at_h: state.clock_h,
        published_turn: state.turn,
        remaining_curve: construireCourbe(classe, vuesPic, slot?.phase),
        curve_cursor: 0,
        evergreen: classe === "evergreen"
            ? { anchor_h: state.clock_h, weekly_rate: tauxRenteFige(mods, false, dansLaNiche) }
            : null,
        series_id: null,
        visibility: "public",
        total_views: 0,
        total_subs: 0,
        frac_views: 0,
        frac_subs: 0,
        frac_money: 0,
        frac_xp: 0,
    };
    // — 6. empilement, image de chaîne —
    state.videos.push(video);
    state.alignment_history.push(alignement);
    if (state.alignment_history.length > HISTORIQUE_ALIGNEMENT_MAX) {
        state.alignment_history = state.alignment_history.slice(-HISTORIQUE_ALIGNEMENT_MAX);
    }
    recalculerImage(state);
    // — 7. jauges (§5.1) —
    const ecart = state.counters.last_publication_h === null
        ? Number.POSITIVE_INFINITY
        : state.clock_h - state.counters.last_publication_h;
    // ⟦A-19⟧ `H_SILENCE_TOLERE`, pas « 48 » : la règle est en JOURS (PLAN §4.2, « publier
    //   ≤ 3 j simulés d'écart »). Écrit en dur, le seuil serait passé de 3 jours à 2.
    if (ecart <= H_SILENCE_TOLERE)
        state.momentum = Math.min(1.5, state.momentum + MOMENTUM_PUBLICATION);
    // « Il est revenu en forme » (A-2 point 3) : Fidélité +5 si la vidéo de retour a la Qualité.
    bonusRetourEnForme(state, retour, outcome.qualite_eff);
    // ⟦A-25⟧ `QUALITE_LONGUE_REUSSIE`, pas `QUALITE_EVERGREEN` : c'est la règle §5.1, elle vaut
    // 1,30 et elle ne bouge pas. Les deux constantes étaient confondues par égalité de valeur.
    // ⟦A-4 §2 / A-26⟧ Les deux gains de Fidélité de la publication passent par le trait de
    // genre. Ce sont les DEUX sources principales de Fidélité de tout l'arc 1 : les contourner
    // revenait à ne jamais servir l'avantage de la créatrice (constat B4 d'AUDIT-FORMULES).
    if (format === "longue" && outcome.qualite_eff >= QUALITE_LONGUE_REUSSIE) {
        state.loyalty += appliquerTraitJauge(state.genre, "loyalty", 3);
    }
    if (bande === "lumiere")
        state.loyalty += appliquerTraitJauge(state.genre, "loyalty", 1);
    // ⟦SPEC-MOTEUR §7.4 · A-4 C⟧ **La prime de Fidélité de la niche.** Elle passe par le trait de
    // genre comme les deux gains voisins ⟦A-4 §2 / A-26⟧ : contourner `appliquerTraitJauge` ici
    // aurait fait une troisième source de Fidélité que l'avantage de la créatrice ne sert pas,
    // c'est-à-dire exactement le constat B4 d'AUDIT-FORMULES, une deuxième fois.
    if (dansLaNiche)
        state.loyalty += appliquerTraitJauge(state.genre, "loyalty", NICHE_FIDELITE);
    if (format === "short") {
        const rang = state.counters.consecutive_shorts + 1;
        if (rang > 3)
            state.loyalty -= 2; // shorts en excès (PLAN §4.2)
    }
    if (classe === "nulle") {
        const flopsConsecutifs = state.counters.consecutive_flops + 1;
        state.mental -= flopsConsecutifs >= 2 ? 8 : 5;
        state.loyalty -= 2;
    }
    // Revenus fixes par vidéo apportés par le contenu (sponsors), si l'argent existe (A-1).
    const sponso = mods.moneyPerVideo?.(format) ?? mods.money_per_video;
    if (sponso > 0 && state.flags.grand_saut_done) {
        state.money += sponso;
        // ⟦SPEC-AXE-PRODUIT §3.1⟧ Un sponsor est un **revenu** : il grossit le dénominateur de S1,
        //   donc il rend la même dépense de production moins « produite ». C'est voulu — une
        //   chaîne qui gagne beaucoup et réinvestit peu reste artisanale dans sa façon de faire.
        noterRevenu(state, content, sponso);
    }
    // — 8. compteurs —
    state.counters.last_publication_h = state.clock_h;
    // ⟦A-130 · C12⟧ Une publication rompt aussi le silence du Fantôme — voir `last_activity_h`
    // (`types.ts`) et `heuresDeSilenceFantome` (`sleep.ts`) : les deux compteurs avancent
    // ensemble ici, seul un jalon raté (`jalons.ts`) avance `last_activity_h` sans celui-ci.
    state.counters.last_activity_h = state.clock_h;
    state.counters.videos_published += 1;
    state.counters.consecutive_shorts = format === "short" ? state.counters.consecutive_shorts + 1 : 0;
    state.counters.consecutive_flops = classe === "nulle" ? state.counters.consecutive_flops + 1 : 0;
    if (state.counters.last_theme === sujet.theme) {
        state.counters.consecutive_same_theme += 1;
    }
    else {
        state.counters.consecutive_same_theme = 1;
        state.counters.last_theme = sujet.theme;
    }
    // 3 vidéos de suite sur le même thème ⇒ thème épuisé 14 jours (§2.4).
    if (state.counters.consecutive_same_theme >= 3) {
        state.counters.theme_cooldowns[sujet.theme] = state.clock_h + H_THEME_EPUISE;
        state.counters.consecutive_same_theme = 0;
    }
    // ⟦SPEC-MOTEUR §7.4 · A-4 C⟧ **La fenêtre de la niche**, juste après le compteur qu'elle ne
    // remplace pas. Les deux cohabitent : l'un brûle un thème répété trois fois d'affilée,
    // l'autre reconnaît une ligne éditoriale sur cinq vidéos. C'est leur désaccord qui fait le
    // rythme du jeu — alterner DANS sa niche vaut mieux que s'y acharner.
    const bascule = enregistrerThemePublie(state, sujet.theme);
    const ligne = ligneNiche(bascule);
    if (ligne !== null)
        ctx.log.push({ turn: state.turn, clock_h: state.clock_h, famille: "chaine", text: ligne });
    // — events et log —
    if (viral) {
        ctx.events.push({
            kind: "viral",
            data: { video_id: video.id, p: pViral, chance: VIRAL_CHANCE, vues_pic: vuesPic },
        });
    }
    ctx.events.push({
        kind: "video_published",
        data: {
            video_id: video.id,
            format,
            classe,
            execution_score: Math.round(outcome.execution_score * 10) / 10,
            vues_pic: vuesPic,
            viral,
        },
    });
    ctx.log.push({
        turn: state.turn,
        clock_h: state.clock_h,
        famille: "chaine",
        text: viral
            ? `Ça part en vrille : « ${sujet.id} » explose (${vuesPic.toLocaleString("fr-FR")} vues attendues).`
            : classe === "nulle"
                ? `« ${sujet.id} » fait un flop. On efface et on recommence.`
                : `Publié : « ${sujet.id} » (${classe}, ${vuesPic.toLocaleString("fr-FR")} vues attendues).`,
    });
    return video;
}
//# sourceMappingURL=publish.js.map